import { Injectable, OnDestroy } from "@angular/core";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Calendar } from "src/model/class/general classes/calendar";
import { HttpClient } from "@angular/common/http";
import { LocalStorageService } from "./local-storage.service";
import { environment } from "src/environments/environment";
import {
  BehaviorSubject,
  Subscription,
  throwError,
  of,
  Observable,
} from "rxjs";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { EventService } from "./event.service";
import { MqttClientService } from "./mqtt-client.service";
import { map, switchMap, debounceTime, filter } from "rxjs/operators";
import { IMqttMessage } from "ngx-mqtt";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import moment from "moment";
import { AuthenticationService } from "./authentication.service";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { LoggerService } from "./logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class CalendarService
  extends DataStorage
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Calendar.service";
  private _calendarStore: GenericApiData = null;
  private _calendarSubject: BehaviorSubject<Array<Calendar>> =
    new BehaviorSubject<Array<Calendar>>(null);
  private _needToInitialize: boolean = true;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  /**Flag property; if the service ha foud an error in the last API call needed for get the calendar-list from the server, it is set to TRUE;
   * otherwise, it is set to FALSE.
   */
  private _isErrorInGetcalendarListAPIRequest: boolean = false;

  get isErrorInGetcalendarListAPIRequest(): boolean {
    return this._isErrorInGetcalendarListAPIRequest;
  }

  _eventSubscriptions: Array<Subscription> = null;

  public readonly calendarList = this._calendarSubject.asObservable();

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _eventService: EventService,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService
  ) {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    /** Subscrive event from Event Service */
    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /**
   * Event destroy service
   */
  ngOnDestroy() {
    this._calendarSubject.next(null);
    this._calendarSubject.complete();

    this.unsubscribeToMQTTAddresses();

    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_CALENDAR);

        await this.unsubscribeToMQTTAddresses();

        this._calendarStore = null;
        this._calendarSubject.next(null);
        this._needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnLoginPlantDid",
          "REACT"
        );
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  Init() {
    if (this._needToInitialize) {
      this.initializeCalendarList();

      this._needToInitialize = false;
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this._needToInitialize = true;

    this.Init();
  }

  //#region MQTT event

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Calendar event
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CALENDAR_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._calendarStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling event: when this fired multiple times
           * it perform execeution without waiting previous execution complete
           */
          if (message.length === 3) {
            const operationKind = parseInt(message[0]);
            const idCalendar = parseInt(message[1]);
            const clientID = message[2];

            /** Finding operation kind: Insert, Update, Delete */
            if (operationKind === GenericOperationKind.INSERT) {
              /** Retrive insert calendar from api */
              const insertCalendar: Calendar = await this.GetCalendarByID(
                idCalendar
              ).catch((error) => {
                return null;
              });

              /** Catching error in previous API call */
              if (insertCalendar) {
                this._calendarStore.data.push(insertCalendar);
              } else {
                throw new Error(
                  "OnInsertCalendar - Error retriving calendar with id= " +
                  idCalendar
                );
              }
            } else if (operationKind === GenericOperationKind.UPDATE) {
              const indexCalendar = this._calendarStore.data.findIndex(
                (calendar: Calendar) => calendar.id === idCalendar
              );

              /** Retrive update calendar from api */
              if (indexCalendar >= 0) {
                const updateCalendar: Calendar = await this.GetCalendarByID(
                  idCalendar
                ).catch((error) => {
                  return null;
                });

                /** Catching error in previous API call */
                if (updateCalendar) {
                  this._calendarStore.data[indexCalendar] = updateCalendar;
                } else {
                  throw new Error(
                    "OnUpdateCalendar Error retriving calendar with id= " +
                    idCalendar
                  );
                }
              } else {
                throw new Error(
                  "OnUpdateCalendar Calendar not found id= " + idCalendar
                );
              }
            } else if (operationKind === GenericOperationKind.DELETE) {
              /**  update store data deleting calendar */
              this._calendarStore.data = this._calendarStore.data.filter(
                (calendar: Calendar) => calendar.id !== idCalendar
              );
            }
            return idCalendar + "|" + clientID;
          } else {
            throw new Error("OnCalendarUpdate - Incorrect format message");
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDBigCalendChanged) => {
          promiseIDBigCalendChanged.then((IDBigCalendChanged_ClientID) => {
            /** Update store with calendar list changed and propagate modify */
            this._calendarStore.lastAPIModified = moment().format(
              MomentFormats.MOMENT_DATETIME_FORMAT
            );

            this._eventService.OnCalendarsChange.next(
              IDBigCalendChanged_ClientID
            );

            this._calendarSubject.next(this._calendarStore.data);
            this.setToStorage(this._calendarStore);
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            " AppReserved.RESERVED_DB_CALENDAR_CHANGED",
            "Error: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Calendar API operations
  InsertCalendar(insertCalendar: Calendar) {
    /**
     *
     *  TO DO
     *
     */
  }

  UpdateCalendar(updateCalendar: Calendar) {
    /**
     *
     *  TO DO
     *
     */
  }

  DeleteCalendar(idCalendar: number) {
    /**
     *
     *  TO DO
     *
     */
  }

  /**
   *
   * Get calendars data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetcalendarListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   *
   */
  private GetCalendars(): Subscription {
    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CALENDAR
      )
      .subscribe(
        (returnCalendarStore: GenericApiData) => {
          this._isErrorInGetcalendarListAPIRequest = false;

          this._calendarStore = returnCalendarStore;
          this._calendarSubject.next(returnCalendarStore.data);

          this.setToStorage(returnCalendarStore);
        },
        (error) => {
          this._isErrorInGetcalendarListAPIRequest = true;
          this._calendarStore = null;
          this._calendarSubject.next([]);
        }
      );
  }

  /**
   * Get calendars data from API by last modify.
   * If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetSupervisionListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array).
   * @param calendarStore
   * @returns
   */
  private GetCalendarsByLastModify(calendarStore: GenericApiData) {
    const queryString: string =
      "?lastModified=" + encodeURIComponent(calendarStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CALENDAR + queryString
      )
      .subscribe(
        (returnCalendarStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnCalendarStore = null)
          this._isErrorInGetcalendarListAPIRequest = false;

          if (returnCalendarStore) {
            calendarStore = returnCalendarStore;

            this.setToStorage(calendarStore);
          }

          this._calendarStore = calendarStore;
          this._calendarSubject.next(calendarStore.data);
        },
        (error) => {
          this._isErrorInGetcalendarListAPIRequest = true;
          //this._scheduleStore = null;
          this._calendarSubject.next([]);
        }
      );
  }

  /**
   * Get single calendar by ID from API.
   *  If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  private async GetCalendarByID(idCalendar: number) {
    const queryString: string = "?id=" + idCalendar;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CALENDAR + queryString
      )
      .pipe(
        switchMap((returnCalendarList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  scenario is finded
           */

          if (returnCalendarList.data.length > 0) {
            const returnCalendar: Calendar = returnCalendarList.data[0];

            return of(returnCalendar);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnCalendar: Calendar) => {
          return returnCalendar;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  /**
   *
   *
   * Initialize calendars list: first it read from storage (via getFromStorage() function)
   * then if storage read failed it tries to retrive data from API
   *
   */
  private initializeCalendarList() {
    this.getFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetCalendars();
      } else {
        this.GetCalendarsByLastModify(resultReadStorage);
      }
    });
  }

  //#region  Storage
  protected getFromStorage(): Promise<object> {
    return this._storageService.get(AppSettings.KEY_CALENDAR).then(
      (calendarDataStore: GenericApiData) => {
        if (calendarDataStore) {
          return calendarDataStore;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(calendarStore: GenericApiData) {
    this._storageService.set(AppSettings.KEY_CALENDAR, calendarStore);
  }
  //#endregion
}
